import { BottomBar, StepIndicatorState } from '@pages/Trips/CreateTripFlow/core/presentation'
import { DayHeaderInput, DestinationSegment, dayLead, dayLine, spanIndexForDay } from '../../core/dayHeader'
import { EventCreateDraft, canLeaveWhatStep, eventName } from './draft'

/**
 * Every label and rule the create-event screens read off the Draft, kept out
 * of the components so it tests in node. Mirrors iOS `EventCreatePresentation`
 * and `EventCreateFlowModel`, on the create-trip flow's step indicator and
 * bottom bar.
 */

export type EventCreateStep = 'what' | 'when' | 'extras'

export const STEPS: EventCreateStep[] = ['what', 'when', 'extras']

/** Step-indicator label for a step. */
export const stepLabel = (step: EventCreateStep): string => {
	switch (step) {
		case 'what':
			return 'What'
		case 'when':
			return 'When'
		case 'extras':
			return 'Extras'
	}
}

export const nextStep = (step: EventCreateStep): EventCreateStep | null => STEPS[STEPS.indexOf(step) + 1] ?? null

export const previousStep = (step: EventCreateStep): EventCreateStep | null => STEPS[STEPS.indexOf(step) - 1] ?? null

/** Only the last step creates the event. */
export const ctaTitle = (step: EventCreateStep): string => (nextStep(step) ? 'Next' : 'Create Event')

/** The header's back chevron: every step but the first. */
export const showsHeaderBack = (step: EventCreateStep): boolean => previousStep(step) !== null

/**
 * Nothing past What until the event has a name — a place or a title — so the
 * indicator can't skip what the backend requires. Once it has one, any step
 * can be jumped to, Extras included: only "Create Event" creates the event.
 */
export const canGoTo = (draft: EventCreateDraft, target: EventCreateStep): boolean =>
	target === 'what' || canLeaveWhatStep(draft)

/**
 * How a step reads in the indicator. Unlike the create-trip flow, the last
 * step can be jumped to too. Steps ahead stay locked — the lightest grey,
 * untappable — until the event has a name; behind, What reads as skipped
 * without one.
 */
export const stepIndicatorState = (
	draft: EventCreateDraft,
	candidate: EventCreateStep,
	current: EventCreateStep,
): StepIndicatorState => {
	if (candidate === current) return 'active'
	const named = canLeaveWhatStep(draft)
	if (STEPS.indexOf(candidate) > STEPS.indexOf(current)) return named ? 'upcoming' : 'unreachable'
	return candidate !== 'what' || named ? 'completed' : 'skipped'
}

// MARK: What

export const TITLE_PLACEHOLDER = 'e.g. Sunset picnic'
export const TITLE_HELPER = "Pick a place and we'll name it for you."
export const CATEGORY_GUESSED_MARKER = 'guessed from the place'

// MARK: When

/** The trip's days, as the When step's calendar lays and tints them. */
export interface TripDays {
	startDate: string | null | undefined
	dayCount: number
	segments: DestinationSegment[]
}

/**
 * A trip day's tint on the When step's calendar, as the edit-trip calendar
 * tints the same trip: the owning destination's position (even and odd
 * alternate), neutral past the destinations, and the first tint across a trip
 * with no places at all.
 */
export const calendarDayFill = (segments: DestinationSegment[], dayIndex: number): number | 'uncovered' => {
	const position = spanIndexForDay(segments, dayIndex)
	return position !== -1 ? position : segments.length ? 'uncovered' : 0
}

/** The whole day in one line, for the bottom bar: its date (or trip day), then its line — "Wed, Oct 7 · Beach Day · Day 2 in Kyoto". */
export const daySummary = (input: DayHeaderInput): string =>
	[dayLead(input.dayIndex, input.startDate), dayLine(input)].filter(Boolean).join(' · ')

// MARK: Extras

export const ATTACHMENTS_SUBTITLE = 'Tickets, menus, screenshots, or anything else'
export const LINKS_SUBTITLE = 'Paste from any app'
export const NOTES_SUBTITLE = 'Any other details or things to remember'

// MARK: Bottom bar

/** The event so far and its day, with the step's CTA — off only until the event has a name. */
export const bottomBar = (draft: EventCreateDraft, step: EventCreateStep, day: string): BottomBar => ({
	title: eventName(draft) || 'New Event',
	subtitle: day,
	ctaTitle: ctaTitle(step),
	ctaStyle: 'primary',
	ctaEnabled: canLeaveWhatStep(draft),
	showsBack: false,
})
